import React, { isValidElement, memo, useEffect, useState } from 'react';
import { omit } from 'lodash-es';
import { styled, Avatar as MuiAvatar, Badge as MuiBadge, Box, } from '@mui/material';
import clsx from 'clsx';
import { IconAway, IconBlocked, IconBusy, IconDoNotDisturb, IconInCalendar, IconInCall, IconInMeeting, IconMobile, IconOffline, IconOnline, IconOutOfOffice, } from '../../internal/icons';
import { avatarClasses, getStatusStyle, getStyle } from './style';
import { useTranslation } from '../../hooks/useTranslation';
import { isCustomSize, isSize, transformSizeByNumber } from './theme';
import { clipPathIdMap, sizeToClipPathMap, statusToShapMap } from './constant';
const getNameAbbreviation = (text) => {
    if (!text) {
        return '';
    }
    const paths = text
        .toUpperCase()
        .trim()
        .split(/\s+/, 2)
        .filter((t) => !!t);
    if (paths.length === 1) {
        return paths[0][0];
    }
    if (paths.length >= 1) {
        const textContent = paths[0][0] + paths[1][0];
        if (/[\u4E00-\u9FA5]+/.test(text)) {
            return paths[0][0];
        }
        return textContent;
    }
    return '';
};
const BadgeIcon = memo(({ status }) => {
    switch (status) {
        case 'online':
            return React.createElement(IconOnline, null);
        case 'offline':
            return React.createElement(IconOffline, null);
        case 'mobile':
            return React.createElement(IconMobile, null);
        case 'inCall':
            return React.createElement(IconInCall, null);
        case 'inMeeting':
            return React.createElement(IconInMeeting, null);
        case 'outOfOffice':
            return React.createElement(IconOutOfOffice, null);
        case 'doNotDisturb':
            return React.createElement(IconDoNotDisturb, null);
        case 'inACalendarEvent':
            return React.createElement(IconInCalendar, null);
        case 'away':
            return React.createElement(IconAway, null);
        case 'block':
            return React.createElement(IconBlocked, null);
        case 'busy':
            return React.createElement(IconBusy, null);
        default:
            return null;
    }
});
const StyledAvatarStatus = styled(MuiBadge)((props) => {
    const { theme, size, status } = props;
    return getStatusStyle(theme, { status, size });
});
const AvatarBase = (props) => {
    const { children, src, displayName } = props;
    const classes = {
        root: avatarClasses.root,
    };
    let abbreviation;
    if (displayName) {
        abbreviation = getNameAbbreviation(displayName);
    }
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    // When an Avatar has an image, the background color may exceed the image area, creating a visual border.
    // After the image is successfully loaded, change the default background color to transparent
    const slotProps = {
        ...(src
            ? {
                img: {
                    onLoad: () => {
                        setImageLoaded(true);
                        setImageError(false);
                    },
                    onError: () => {
                        setImageError(true);
                        setImageLoaded(false);
                    },
                },
            }
            : {}),
    };
    useEffect(() => {
        // If the image src has changed and the src is invalid, it is set to not be loaded successfully.
        if (!src) {
            setImageLoaded(false);
            setImageError(false);
        }
    }, [src]);
    // Always show children if they are provided, regardless of src
    if (children) {
        return (React.createElement(React.Fragment, null,
            React.createElement(MuiAvatar, { variant: "rounded", classes: classes, "aria-hidden": true, slotProps: slotProps, className: clsx({ [avatarClasses.imageLoaded]: imageLoaded }) }, children),
            React.createElement(Box, { className: avatarClasses.mask })));
    }
    // Show abbreviation if no src or src has error
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    if ((!src && abbreviation) || (src && imageError && abbreviation)) {
        return (React.createElement(React.Fragment, null,
            React.createElement(MuiAvatar, { variant: "rounded", classes: classes, "aria-hidden": true, slotProps: slotProps, className: clsx({ [avatarClasses.imageLoaded]: imageLoaded }), src: imageError ? undefined : src }, abbreviation),
            React.createElement(Box, { className: avatarClasses.mask })));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(MuiAvatar, { variant: "rounded", classes: classes, src: src, "aria-hidden": true, slotProps: slotProps, className: clsx({ [avatarClasses.imageLoaded]: imageLoaded }) }),
        React.createElement("div", { className: avatarClasses.mask })));
};
const AvatarWithStatus = React.forwardRef((props, ref) => {
    const { status, size = 'medium', sx, className, displayName, inactive, onClick, 'aria-label': ariaLabel, ariaLabel: _ariaLabel, tabIndex, ...rest } = props;
    const _size = isSize(size) ? size : transformSizeByNumber(size);
    const containerClass = clsx(avatarClasses.container, className, {
        [`${avatarClasses.text}`]: !!displayName,
    }, {
        [`${avatarClasses.inactive}`]: inactive,
    }, {
        [`${avatarClasses.extrasSmall}`]: _size === 'extraSmall',
    }, {
        [`${avatarClasses.small}`]: _size === 'small',
    }, {
        [`${avatarClasses.medium}`]: _size === 'medium',
    }, {
        [`${avatarClasses.large}`]: _size === 'large',
    }, {
        [`${avatarClasses.extraLarge}`]: _size === 'extraLarge',
    });
    const { t } = useTranslation('Avatar');
    useEffect(() => {
        if (!status || !isCustomSize(size) || typeof status !== 'string')
            return;
        const id = clipPathIdMap[statusToShapMap[status]];
        const path = sizeToClipPathMap.large[statusToShapMap[status]];
        // inject extraLarge svg
        if (!document.querySelector(`#${id}`)) {
            const svgContent = `<svg width="0" height="0" viewBox="0 0 148 147" version="1.1" xmlns="http://www.w3.org/2000/svg" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:2;">
  <clipPath id="${id}" clipPathUnits="objectBoundingBox">
    <path transform="scale(0.025, 0.025)" d="${path}" style="fill:none;" />
  </clipPath>
</svg>`;
            const svg = document.createElement('div');
            svg.innerHTML = svgContent;
            svg.style.cssText = `
        height: 0;
        overflow: hidden;
      `;
            document.body.appendChild(svg);
        }
    }, [_size, size, status]);
    const commonProps = {
        component: onClick ? 'button' : 'div',
        className: containerClass,
        sx,
        tabIndex: tabIndex ?? 0,
        role: onClick ? 'button' : 'img',
        onClick,
        'aria-label': ariaLabel ?? _ariaLabel,
        ...omit(rest, 'style'),
    };
    if (!commonProps['aria-label']) {
        let label = displayName
            ? `${t('profileAvatar', { name: displayName })}`
            : t('defaultProfileAvatar');
        if (typeof status === 'string') {
            // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
            const statusLabel = t(status);
            if (statusLabel) {
                label += `, ${statusLabel}`;
            }
        }
        else if (isValidElement(status)) {
            if (status.props['aria-label']) {
                label += `, ${status.props['aria-label']}`;
            }
        }
        if (inactive) {
            label += `, ${t('inactive')}`;
        }
        commonProps['aria-label'] = label;
    }
    if (!status || ['extraSmall'].includes(_size)) {
        // 'extraSmall' Avatars currently do not support status
        return (React.createElement(Box, { ...commonProps, ref: ref },
            React.createElement(AvatarBase, { size: size, className: className, displayName: displayName, ...rest })));
    }
    if (typeof status === 'string') {
        const badgeContent = React.createElement(BadgeIcon, { status: status });
        return (React.createElement(Box, { ...commonProps, ref: ref },
            React.createElement(StyledAvatarStatus, { status: status, classes: {
                    root: clsx(avatarClasses.statusRoot, className),
                    badge: clsx(avatarClasses.statusBadge, avatarClasses.status(status)),
                }, size: size, badgeContent: badgeContent, anchorOrigin: { vertical: 'top', horizontal: 'right' }, "aria-hidden": true },
                React.createElement(AvatarBase, { size: size, className: className, displayName: displayName, ...rest }))));
    }
    return (React.createElement(Box, { ...commonProps, ref: ref },
        React.createElement(StyledAvatarStatus, { status: status, classes: {
                root: clsx(avatarClasses.statusRoot, className),
                badge: avatarClasses.statusBadge,
            }, badgeContent: status, anchorOrigin: { vertical: 'top', horizontal: 'right' }, size: size, "aria-hidden": true },
            React.createElement(AvatarBase, { size: size, displayName: displayName, ...rest }))));
});
const StyledAvatar = styled(AvatarWithStatus)((props) => {
    const { theme, size, displayName, status, color, backgroundColor } = props;
    return getStyle(theme, {
        size,
        abbreviation: getNameAbbreviation(displayName),
        status,
        color,
        backgroundColor,
    });
});
//  The exposed components must support the use of ref.
const Avatar = React.forwardRef((props, ref) => (React.createElement(StyledAvatar, { ...props, ref: ref })));
export default Avatar;
